import React, { useState } from 'react';
import { Stack, Text, FontWeights, PrimaryButton, DefaultButton, StackItem, TextField, DatePicker, ComboBox, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { connect } from 'react-redux';
import { mergeStyles } from 'office-ui-fabric-react/lib/Styling';
import _ from 'lodash';
import moment from 'moment';
import { SET_MEETING_COMMAND, CREATE_MEETING_COMMAND } from './meeting-creator/actions';
import { goBack } from 'connected-react-router';
import { hasValidSubject } from './meeting-creator/validators';
import { FormattedMessage } from 'react-intl';
import { translate } from './localization/translate';
const boldStyle = { root: { fontWeight: FontWeights.semibold } };
function formatDuration(duration) {
    let str = '';
    if (Math.floor(duration.asDays()) > 0) {
        str += translate('meetingPage.duration.days', { days: Math.floor(duration.asDays()) });
    }
    if (duration.hours() > 0) {
        str += translate('meetingPage.duration.hours', { hours: duration.hours() });
    }
    if (duration.minutes() > 0) {
        str += translate('meetingPage.duration.minutes', { minutes: duration.minutes() });
    }
    return str;
}
const datePickerFormat = 'll';
const timePickerFormat = 'LT';
const meetingIconClass = mergeStyles({
    fontSize: 16,
    height: 16,
    width: 16,
    margin: '0 7px',
    position: 'relative',
    top: 1,
    backgroundColor: '#6264a7',
    padding: '10px',
    color: '#fff',
    borderRadius: '3px'
});
const inputIconClass = mergeStyles({
    position: 'relative',
    top: 7
});
function DateTimePicker(props) {
    var _a, _b, _c;
    function getDatePickerStrings() {
        const localeData = moment.localeData();
        return {
            months: localeData.months(),
            shortMonths: localeData.monthsShort(),
            days: localeData.weekdays(),
            shortDays: localeData.weekdaysMin(),
            goToToday: translate('datePicker.goToToday'),
            prevMonthAriaLabel: translate('datePicker.previousMonth.ariaLabel'),
            nextMonthAriaLabel: translate('datePicker.nextMonth.ariaLabel'),
            prevYearAriaLabel: translate('datePicker.previousYear.ariaLabel'),
            nextYearAriaLabel: translate('datePicker.nextYear.ariaLabel'),
            closeButtonAriaLabel: translate('datePicker.close.ariaLabel'),
        };
    }
    function onDayPicked(date) {
        const currentDateTime = moment(props.dateTime);
        const offsetFromStartOfDay = currentDateTime.diff(moment(currentDateTime).startOf('day'));
        const newDateTime = moment(date !== null && date !== void 0 ? date : currentDateTime)
            .startOf('day')
            .add(offsetFromStartOfDay);
        props.onTimeUpdated(newDateTime);
    }
    function onTimePicked(event, option, index, value) {
        const currentDateTimeStartOfDay = moment(props.dateTime).startOf('day');
        let newDateTime;
        if (option) {
            const offsetFromStartOfDay = moment.duration(option.key, 'minutes');
            newDateTime = currentDateTimeStartOfDay.add(offsetFromStartOfDay);
        }
        else {
            // User entered a free-form string, try to parse it as a time
            const enteredTime = moment(value, timePickerFormat);
            if (enteredTime.isValid()) {
                const offsetFromStartOfDay = enteredTime.diff(moment(enteredTime).startOf('day'));
                newDateTime = currentDateTimeStartOfDay.add(offsetFromStartOfDay);
            }
            else {
                newDateTime = moment(props.dateTime);
            }
        }
        props.onTimeUpdated(newDateTime);
    }
    function onFormatDate(dateToFormat) {
        return moment(dateToFormat).format(datePickerFormat);
    }
    function onParseDateFromString(value) {
        return moment(value, datePickerFormat).toDate();
    }
    const timeSuggestions = _.range(0, 1440, 30).map(minutes => {
        // if the selection is before the min value
        const projectedEndTime = moment(props.dateTime)
            .startOf('day')
            .add(moment.duration(minutes, 'minutes'));
        const isDisabled = moment(props.minDate).isAfter(projectedEndTime);
        const timeTag = moment()
            .startOf('day')
            .minutes(minutes)
            .format(timePickerFormat);
        const projectedDuration = moment.duration(moment(projectedEndTime).diff(props.minDate));
        const projectedDurationString = _.trim(formatDuration(projectedDuration));
        return {
            key: minutes,
            text: props.includeDuration &&
                !isDisabled &&
                projectedDurationString.length > 0
                ? `${timeTag} (${projectedDurationString})`
                : timeTag,
            disabled: isDisabled
        };
    });
    return (React.createElement(Stack, { horizontal: true },
        React.createElement(DatePicker, { className: "newMeetingDatePicker", borderless: true, firstDayOfWeek: moment.localeData().firstDayOfWeek(), strings: getDatePickerStrings(), value: (_a = props.dateTime) === null || _a === void 0 ? void 0 : _a.toDate(), formatDate: onFormatDate, parseDateFromString: onParseDateFromString, onSelectDate: onDayPicked, minDate: (_b = props.minDate) === null || _b === void 0 ? void 0 : _b.toDate() }),
        React.createElement(ComboBox, { className: "newMeetingComboBox", styles: { root: { maxHeight: '500px' } }, useComboBoxAsMenuWidth: !props.includeDuration, scrollSelectedToTop: true, allowFreeform: true, autoComplete: "on", options: timeSuggestions, onChange: onTimePicked, text: (_c = props.dateTime) === null || _c === void 0 ? void 0 : _c.format(timePickerFormat) }),
        props.iconName === 'ReplyAlt' ? (React.createElement(FontIcon, { className: "newMeetingPickerIcon", iconName: props.iconName })) : (React.createElement(Text, { className: "newMeetingPickerIncrement", variant: "smallPlus" }, formatDuration(moment.duration(moment(props.dateTime).diff(moment(props.minDate))))))));
}
const mapStateToProps = (state) => ({
    meeting: state.meeting.inputMeeting,
    creationInProgress: state.meeting.creationInProgress,
    validationFailures: {
        invalidTitle: hasValidSubject(state.meeting.inputMeeting)
            ? undefined
            : translate('meetingPage.invalidSubject.error')
    }
});
const mapDispatchToProps = (dispatch) => ({
    setMeeting: (meeting) => {
        dispatch({
            type: SET_MEETING_COMMAND,
            meeting
        });
    },
    createMeeting: (meeting) => {
        dispatch({
            type: CREATE_MEETING_COMMAND,
            meeting
        });
    },
    cancel: () => dispatch(goBack())
});
function MeetingPageComponent(props) {
    var _a;
    const [validationEnabled, setValidationEnabled] = useState(false);
    function onSubjectChanged(evt, newValue) {
        // The meeting objects are small, cloning is cheap enough
        // Normally would use immutable records or similar to avoid overhead.
        const nextMeeting = _.cloneDeep(props.meeting);
        nextMeeting.subject = newValue !== null && newValue !== void 0 ? newValue : '';
        props.setMeeting(nextMeeting);
    }
    function onStartDateSelected(date) {
        const nextMeeting = _.cloneDeep(props.meeting);
        nextMeeting.startDateTime = date !== null && date !== void 0 ? date : nextMeeting.startDateTime;
        // If start >= end, adjust to be the same delta as before from the start time
        if (nextMeeting.startDateTime.isSameOrAfter(nextMeeting.endDateTime)) {
            const existingDelta = moment(props.meeting.endDateTime).diff(moment(props.meeting.startDateTime));
            const newEndDateTime = moment(nextMeeting.startDateTime).add(existingDelta);
            if (nextMeeting.startDateTime.isSameOrAfter(newEndDateTime)) {
                newEndDateTime.add(existingDelta);
            }
            nextMeeting.endDateTime = newEndDateTime;
        }
        props.setMeeting(nextMeeting);
    }
    function onEndDateSelected(date) {
        const nextMeeting = _.cloneDeep(props.meeting);
        const newEndDateTime = date !== null && date !== void 0 ? date : nextMeeting.endDateTime;
        // Allow the change only if it maintains start < end
        if (!nextMeeting.startDateTime.isAfter(newEndDateTime)) {
            nextMeeting.endDateTime = newEndDateTime;
        }
        props.setMeeting(nextMeeting);
    }
    function onCreate() {
        if (!!props.validationFailures.invalidTitle) {
            setValidationEnabled(true);
            return;
        }
        props.createMeeting(props.meeting);
    }
    if (props.creationInProgress) {
        return (React.createElement("div", { className: "spinnerContainer" },
            React.createElement(Spinner, { size: SpinnerSize.large })));
    }
    return (React.createElement("div", { className: "newMeetingContainer" },
        React.createElement(Stack, { className: "container", verticalFill: true, tokens: {
                childrenGap: 35
            } },
            React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 15 } },
                React.createElement(StackItem, { grow: true },
                    React.createElement(FontIcon, { iconName: "Calendar", className: meetingIconClass }),
                    React.createElement(Text, { variant: "xLarge", styles: boldStyle },
                        React.createElement(FormattedMessage, { id: "meetingPage.header" }))),
                React.createElement(StackItem, { align: "end", className: "newMeetingButtons" },
                    React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 10 } },
                        React.createElement(PrimaryButton, { className: "teamsButton", disabled: props.creationInProgress, onClick: () => onCreate(), ariaLabel: translate('meetingPage.create.ariaLabel') },
                            React.createElement(FormattedMessage, { id: "meetingPage.create" })),
                        React.createElement(DefaultButton, { className: "teamsButtonInverted", disabled: props.creationInProgress, onClick: () => props.cancel(), ariaLabel: translate('meetingPage.cancel.ariaLabel') },
                            React.createElement(FormattedMessage, { id: "meetingPage.cancel" }))))),
            React.createElement(Stack, { horizontal: true },
                React.createElement(StackItem, { className: "newMeetingInputIcon" },
                    React.createElement(FontIcon, { iconName: "Edit", className: inputIconClass })),
                React.createElement(StackItem, { grow: true },
                    React.createElement(TextField, { className: "newMeetingInput", placeholder: translate('meetingPage.title.input'), value: (_a = props.meeting) === null || _a === void 0 ? void 0 : _a.subject, underlined: true, onChange: onSubjectChanged, errorMessage: validationEnabled
                            ? props.validationFailures.invalidTitle
                            : undefined }))),
            React.createElement("div", { className: "newMeetingDatePickerContainer" },
                React.createElement(FontIcon, { iconName: "Clock", className: inputIconClass }),
                React.createElement("div", { className: "newMeetingPicker" },
                    React.createElement(DateTimePicker, { dateTime: props.meeting.startDateTime, minDate: moment(), onTimeUpdated: onStartDateSelected, includeDuration: false, iconName: "ReplyAlt" }),
                    React.createElement(DateTimePicker, { dateTime: props.meeting.endDateTime, minDate: props.meeting.startDateTime, onTimeUpdated: onEndDateSelected, includeDuration: true })))),
        React.createElement(StackItem, { className: "newMeetingButtonsMobile" },
            React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 10 } },
                React.createElement(PrimaryButton, { className: "teamsButton teamsButtonFullWidth", disabled: props.creationInProgress, onClick: () => onCreate(), ariaLabel: translate('meetingPage.create.ariaLabel') },
                    React.createElement(FormattedMessage, { id: "meetingPage.create" })),
                React.createElement(DefaultButton, { className: "teamsButtonInverted teamsButtonFullWidth", disabled: props.creationInProgress, onClick: () => props.cancel(), ariaLabel: translate('meetingPage.cancel.ariaLabel') },
                    React.createElement(FormattedMessage, { id: "meetingPage.cancel" }))))));
}
export default connect(mapStateToProps, mapDispatchToProps)(MeetingPageComponent);
